import {
  endOfDay,
  parseISO,
  startOfDay,
  subMonths,
} from 'date-fns';

import * as constants from './constants';

const MAX_ASSETS_MONTHS_OFFSET = 1;

/**
 * Timerange based on assets availability time
 *
 * @param  {Date}   endDate
 * @return {Object} offset
 * @return {Date}   offset.startDate - first day when assets were created
 * @return {Date}   offset.endDate   - last day when assets of assets ('now' by default)
 * }
 */
export const getAssetsTimeOffset = (endDate = new Date()) => {
  const startDate = subMonths(endDate, MAX_ASSETS_MONTHS_OFFSET);
  return { startDate: startOfDay(startDate), endDate: endOfDay(endDate) };
};

/**
 * Append Date filter if doesn't exist
 * @param {string} payload - query value
 *
 * @return {string} enhanced query value
 */
export const parsePhrase = (payload) => {
  let finalQuery = payload;
  if (!payload.includes('date:')) {
    const { startDate, endDate } = getAssetsTimeOffset();
    finalQuery += ` AND date: [${startDate.toISOString()} TO ${endDate.toISOString()}]`;
  }
  return finalQuery;
};

/**
 * Create a date range string for date filter
 * Use startOfDay and endOfDay only if manual time is not available
 * @param {(string|Date)[]} - start and end string-dates or dates in YYYY-MM-DD format
 *
 * @return {string} - date range valid for date filter
 */
export const buildDateRange = ([startDate, endDate]) => {
  const parsedStartDate = (startDate instanceof Date ? startDate : parseISO(startDate));
  const parsedEndDate = (endDate instanceof Date ? endDate : parseISO(endDate));
  const isManualTimeAvailable = parsedStartDate.toISOString().indexOf('T00:00:00.000Z') < 0;
  const normalizedStartDate = (
    isManualTimeAvailable ? parsedStartDate : startOfDay(parsedStartDate)
  ).toISOString();
  const normalizedEndDate = (
    isManualTimeAvailable ? parsedEndDate : endOfDay(parsedEndDate)
  ).toISOString();

  return `[${normalizedStartDate} TO ${normalizedEndDate}]`;
};

/**
 * Transform query phrase to valid filter format: [filterName, filterValue]
 * @param {Object} query
 * @param {string} query.connectorSeparator - last char of the filter - closing bracket
 * @param {string} query.inputSplit - separator between values inside the filter,
 *                 probably ' ' or '"'
 * @param {string} query.inputKey - filter name
 * @param {string} query.inputValue - filter value
 *
 * @return {string[]} - two element array with filter name and value
 */
export const normalizeQueryPhraseToFilters = ({
  connectorSeparator, inputSplit, inputKey, inputValue,
}) => {
  const [val, connector] = inputValue.split(connectorSeparator);
  const inputValues = val.replace('(', '').split(inputSplit).filter((el) => el.trim());
  return [inputKey, `("${inputValues.join('" "')}") ${connector}`];
};

/**
 * Return range of dates starting with beginning of day and ending with end of some other day
 * @param {Date} start - start date
 * @param {Date} end   - end date
 *
 * @return {Date} datetime based on input
 */
export const fullDayRange = (start, end) => ({
  startDate: startOfDay(start),
  endDate: endOfDay(end),
});

/**
 * Create Date from string
 * @param {string} date - string in YYYY-MM-DD format or ISO8601 (full) format
 *
 * @return {Date} datetime based on input
 */
export const parseDate = (date) => new Date(Date.parse(date));

const queryDateRegex = /date\s*:\s*\[\s*([\d-]+)\s+to\s+([\d-]+)\s*\]/i;

/**
 * Changes date filter to UTC tz
 * @param {string} query - value of query input
 *
 * @return {string} value of query input with UTC tz in date filter
 */
export const serializeQueryToUTC = (query) => {
  const datePart = query?.match(queryDateRegex);
  if (datePart?.length >= 3) {
    const [startDate, endDate] = datePart
      .slice(1, 3) // datePart[1] is the start date; datePart[2] is the end date
      .map(parseDate);
    return query.replace(
      queryDateRegex,
      `date: ${buildDateRange([startDate, endDate])}`,
    );
  }

  return query;
};

export const remapFieldNames = (query) => (
  query.replace(/teams:/g, 'team_id:')
);

export const serializeQuery = (query) => (
  remapFieldNames(serializeQueryToUTC(query))
);

/**
 * Parses value from query input to filters format
 * @param {string} payload - value of query input
 *
 * @return {Array[]} array with filters
 *
 * @example
 * for param payload = 'text: foo AND name: bar'
 * should return [['text', ['foo']], ['name', ['bar']]]
 */
export const normalizeQueryInputValue = (payload) => {
  if (!payload) {
    return [];
  }

  return payload
    .split(/\sAND|OR\s/gi)
    .map((el) => el.trim().split(/:(.+)?/g))
    .map(([key, val]) => {
      // this statement should be truthy for both: (" and "
      if (val.includes('"')) {
        return [key, val.split('" "')];
      }
      if (val.includes('(')) {
        return [key, val.split(' ')];
      }
      if (key === constants.DATE_FILTER) {
        return [key, val.replace(/[[\]]/g, '').split(' TO ')];
      }
      return [key, val];
    })
    .map(([key, val]) => {
      const filteredVal = Array.isArray(val) ?
        val
          .map((value) => value.trim().replace(/[(")]/g, ''))
          .filter((value) => value) :
        val.trim();
      return [key.toLowerCase(), filteredVal];
    });
};

/**
 * Filters out values from filters to contain only filters with values inside selectedItems key
 * @param {Object} payload - filters object
 *
 * @return {Array[]} - array with filters with selected Items
 *
 * @example
 * for param payload = {
 *  foo: { selectedItems: ['item'] },
 *  bar: { selectedItems: [], items: ['a', 'b'] },
 * };
 * should return [['foo', ['item']]]
 */
export const getFiltersWithSelectedItems = (payload) => (
  Object.entries(payload).reduce((acc, [filterName, value]) => {
    if (value.selectedItems.length) {
      return [...acc, [filterName, value.selectedItems]];
    }
    return acc;
  }, [])
);

/**
 * Merges value of selected filters with input query string
 * @param {Object} query
 * @param {string} query.input - value of input query
 * @param {Array[]} query.filters - list of filters with selected items
 *
 * @return {string} updated value for query string
 *
 * @example
 * for param payload = {
 *  input: 'owner: norbertsienkiewicz',
 *  filters: [['tag', ['foo123']]],
 * };
 * should return 'owner: norbertsienkiewicz AND tag: ("foo123")'
 */
export const unionQuery = ({ input, filters }) => {
  const inputParts = input
    .split(/(text|name|status|build|owner|platform|browser|tag|date|teams):/gi)
    .filter((el) => el.trim());

  const usedFilterNamesInInput = [];
  const existingFilters = [];

  /*
    We only want to consume from the input values from allowed phrases but not filters.
    Filters should contain newest changes applied by updateViaQueryInput method, so we can
    easily attach them, without any merging logic.
  */
  for (let i = 0; i < inputParts.length; i += 2) {
    const inputKey = inputParts[i].toLowerCase();
    const inputValue = inputParts[i + 1] || '';
    if (!constants.ALLOWED_PHRASES_BUT_NOT_FILTERS.includes(inputKey)) {
      continue; // eslint-disable-line no-continue
    }

    usedFilterNamesInInput.push(inputKey);

    if (inputKey === constants.DATE_FILTER) {
      return [inputKey, inputValue];
    }

    if (inputValue.includes('")')) {
      const filterElement = normalizeQueryPhraseToFilters({
        connectorSeparator: '")', inputSplit: '"', inputKey, inputValue,
      });
      existingFilters.push(filterElement);
      continue; // eslint-disable-line no-continue
    }

    if (inputValue.includes('(')) {
      const filterElement = normalizeQueryPhraseToFilters({
        connectorSeparator: ')', inputSplit: ' ', inputKey, inputValue,
      });
      existingFilters.push(filterElement);
      continue; // eslint-disable-line no-continue
    }

    let trimmedValue = inputValue.trim();
    let connector = '';

    // remove connector from value to compare with values in filters
    const hasANDConnector = /\s+AND\s*$/gi.test(trimmedValue);
    const hasORConnector = /\s+OR\s*$/gi.test(trimmedValue);

    if (hasANDConnector) {
      trimmedValue = inputValue.replace(/\s+AND/gi, '').trim();
      connector = ' AND ';
    } else if (hasORConnector) {
      trimmedValue = inputValue.replace(/\s+OR/gi, '').trim();
      connector = ' OR ';
    }

    existingFilters.push([inputKey, `${trimmedValue}${connector}`]);
  }

  // attach keys from filters which doesnt exist in inputquery
  const additionalFilters = filters
    // eslint-disable-next-line no-unused-vars
    .filter(([filterName, values]) => !usedFilterNamesInInput.includes(filterName))
    .map(([filterName, values]) => {
      if (filterName === constants.DATE_FILTER) {
        return [filterName, buildDateRange(values)];
      }
      return [filterName, `("${values.join('" "')}")`];
    });

  // convert filters to string
  const result = [
    existingFilters.map((condition) => condition.join(': ')).join(' '),
  ];

  if (additionalFilters.length) {
    if (usedFilterNamesInInput.length && !/AND\s?$/gi.test(result)) {
      result.push(' AND ');
    }
    result.push(additionalFilters.map((condition) => condition.join(': ')).join(' AND '));
  }

  return result.join('').trim().replace(/\s\s/g, ' ');
};
